import './index.scss'
import Footer from '../../components/layouts/footer'
import {ResponsivePageTitle, PageSubTitle, PageTitle} from '../../components/core/title'
import Table from '../../components/core/table'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getRulesets, deleteRuleset} from '../../stores/incident-management'
import ContentLoader from '../../components/core/loader'
import EmptyContent from '../resources/EmptyContent'
import Tooltip from '../../components/core/tooltip'
import {HelpIcon, PlusIcon, EditIcon, TrashIcon, HorizontalMoreIcon} from '../../icons'
import TeamSwitcher from '../../components/pages/teams'
import {ImgButton} from '../../components/core/button'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import {setConfirmIsActive} from '../../stores/alert'
import Confirm from '../../components/core/confirm'
import Customer from '../../utils/customer'
import {colors} from '../../utils/colors'
import {routes} from '../../routes'
import {Link, useNavigate} from 'react-router-dom'
import NavigationHelper from '../../utils/navigation'
import classNames from 'classnames'
import {sortBySelected} from '../../utils/sorting'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const {isReadOnlyModeEnabled} = envConfig

const SORT_ID = 'rulesets'

const Rulesets = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const text = useNamespaceTranslations('rulesets')
    const rulesets = useSelector(state => state.incident_management.rulesets)
    const isFetching = useSelector(state => state.incident_management.isFetching)
    const isDeleting = useSelector(state => state.incident_management.isDeleting)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const teamId = useSelector(state => state.teams.selectedTeamId)
    const [filteredRulesets, setFilteredRulesets] = useState(rulesets)
    const [checkedRulesetsIds, setCheckedRulesetsIds] = useState([])

    const tableHeader = [
        {
            label: 'Name',
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }
            }
        },
        {
            label: 'Type',
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'type_asc',
                    field: ['type']
                },
                desc: {
                    key: 'type_desc',
                    field: ['type']
                }
            }
        },
        {
            label: 'Level',
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'level_asc',
                    field: ['level']
                },
                desc: {
                    key: 'level_desc',
                    field: ['level']
                }
            }
        }
    ]

    const generateTriggers = id => {
        return <Dropdown menuButton={<button className={classNames('table-action-btn', {
            dark: isDarkMode
        })} ><HorizontalMoreIcon/></button>}>
            <MenuItem onClick={() => {
                navigate(NavigationHelper.getNavigationUrl(
                    NavigationHelper.getLocalizedPath(routes.update_ruleset.paths),
                    ':id', id))
                setCheckedRulesetsIds([id])
            }} disabled={isReadOnlyModeEnabled}>
                <span className="d-flex"><EditIcon/></span>
                <span>{text.edit}</span>
            </MenuItem>
            <MenuDivider/>
            <MenuItem className="danger" onClick={() => {
                dispatch(setConfirmIsActive(true))
                setCheckedRulesetsIds([id])
            }
            } disabled={isReadOnlyModeEnabled}>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    const tbodyData = filteredRulesets?.map(item => {
        return [
            <span className="min-width">
                {item?.name || '-'}
                {item?.name && item?.description && <Tooltip placement='right' content={item?.description}>
                    <HelpIcon width={20} height={20} />
                </Tooltip>}
            </span>,
            <span className="min-width">
                {item?.type}
            </span>,
            <span className="flex-1">
                <span className={classNames('badge capitalize', {
                    'badge-teal': item?.level === 'team',
                    'badge-primary': item?.level !== 'team',
                    dark: isDarkMode
                })}>{item?.level}</span>
                <span>{generateTriggers(item?.id)}</span>
            </span>
        ]
    })

    const generateRulesets = () => {
        return isFetching ? <ContentLoader /> :
            <>
                {
                    filteredRulesets?.length > 0 ?
                        <>
                            <div className="search-filter-actions">
                                <div className="selected-actions">
                                    <Link to={NavigationHelper.getLocalizedPath(routes.create_ruleset.paths)}>
                                        <ImgButton className={classNames({
                                            'light-bg': !isDarkMode
                                        })} disabled={isReadOnlyModeEnabled}>
                                            <PlusIcon stroke={colors.colorMuted}/>
                                            <span className="button-text">{text.new}</span>
                                        </ImgButton>
                                    </Link>
                                    <ImgButton className={classNames({
                                        'light-bg': !isDarkMode
                                    })}
                                    disabled={checkedRulesetsIds?.length !== 1 || isReadOnlyModeEnabled}
                                    onClick={() => {
                                        navigate(NavigationHelper.getNavigationUrl(
                                            NavigationHelper.getLocalizedPath(routes.update_ruleset.paths),
                                            ':id',
                                            checkedRulesetsIds?.[0]))
                                    }}
                                    >
                                        <EditIcon/>
                                        <span className="button-text">{text.edit}</span>
                                    </ImgButton>
                                    <ImgButton className={classNames('outline-red', {
                                        'light-bg': !isDarkMode
                                    })}
                                    onClick={() => dispatch(setConfirmIsActive(true))}
                                    disabled={checkedRulesetsIds?.length !== 1 || isReadOnlyModeEnabled}
                                    >
                                        <span><TrashIcon stroke={colors.colorRed}/></span>
                                        <span className="button-text">{text.delete}</span>
                                    </ImgButton>
                                </div>
                            </div>
                            <div className="content-body">
                                <Table
                                    strech
                                    tableHeader={tableHeader}
                                    tbodyData={tbodyData}
                                    data={filteredRulesets}
                                    identificator='id'
                                    inputName='id'
                                    selectedData={checkedRulesetsIds}
                                    isMultiCheckable={false}
                                    hasCheckbox={true}
                                    setSelectedData={data => setCheckedRulesetsIds(data)}
                                    isSortingEnabled
                                    setData={setFilteredRulesets}
                                    sortId={SORT_ID}
                                    originalData={rulesets}
                                />
                            </div>
                        </> : <EmptyContent
                            message={text.empty_content_text}
                            hideButton={true}
                        />
                }
            </>
    }

    const deleteSelectedItem = async() => {
        await dispatch(deleteRuleset(checkedRulesetsIds?.[0]))
        dispatch(getRulesets(false))
        dispatch(setConfirmIsActive(false))
        setCheckedRulesetsIds([])
    }

    useEffect(() => {
        dispatch(getRulesets())
    }, [])

    useEffect(() => {
        setFilteredRulesets(sortBySelected(rulesets, SORT_ID, tableHeader))
    }, [rulesets])

    useEffect(() => {
        setFilteredRulesets(sortBySelected(rulesets?.filter(item => {
            return item?.level === 'team' ?
                item?.level_id === teamId :
                item
        }), SORT_ID, tableHeader))
    }, [teamId])

    return (
        <main className={classNames('main rulesets', {
            dark: isDarkMode
        })}>
            <Confirm
                body={text.are_you_sure}
                showLoader={isDeleting}
                callback={deleteSelectedItem}
            />
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>

                    <Link to={NavigationHelper.getLocalizedPath(routes.create_ruleset.paths)}>
                        <ImgButton disabled={isReadOnlyModeEnabled}>
                            {text.add_new_ruleset}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    </Link>
                </div>
                <div>
                    {Customer.isRoot() && (
                        <TeamSwitcher/>
                    )}
                </div>
                {generateRulesets()}
            </div>
            <Footer/>
        </main>
    )
}

export default Rulesets
